// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { styled } from '@mui/material/styles';

import DocPreview from './DocPreview';
import { uploadFile as fileUpload } from '../../../../../utils/uploadFile';
import useAuth from '../../../../../hooks/useAuth';

const DropZone = styled('div')(({ theme }) => ({
  outline: 'none',
  textAlign: 'center',
  padding: theme.spacing(4, 2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color', 'border-color', 'opacity']),
  '&:hover': { opacity: 0.72 },
}));

const DEFAULT_ACCEPT = 'image/*,.heic,.heif,application/pdf';
const PDF_ONLY_ACCEPT = 'application/pdf';

// Android cloud/email pickers (Drive, OneDrive, Outlook) often hand back a File
// with an empty or application/octet-stream type. Matching on MIME alone rejected
// a perfectly good PDF or photo — and silently, so the user could edit the dates
// but never attach the new document. Fall back to the extension everywhere.
const PDF_EXT = /\.pdf$/i;
const IMAGE_EXT = /\.(jpe?g|png|webp|heic|heif)$/i;

const matchesAccept = (file, accept) => {
  const type = file?.type || '';
  const name = file?.name || '';
  const isPdf = type.includes('pdf') || PDF_EXT.test(name);
  if (accept === PDF_ONLY_ACCEPT) return isPdf;
  return isPdf || type.includes('image') || IMAGE_EXT.test(name);
};

// A finished upload lives only in the parent step's React state until the step is
// submitted. Anything that reloads the tab in between — most often a mobile browser
// killing the tab under memory pressure — silently discards it, and the step then
// reports "Please select a valid document copy!" for a file that uploaded fine and
// is already sitting in blob storage.
//
// So remember the finalized envelope per step, keyed by `persistKey`, and replay it
// once on mount. sessionStorage is per-tab and dies with the browser session, which
// is the right lifetime for "recover the work of the tab that just died".
const STORAGE_PREFIX = 'wesafe.pendingUpload.';
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

// Keyed by OWNER as well as step. sessionStorage is per-tab, and a tab outlives
// a logout: sign out, register a second account in the same tab, and the new
// account's Blue Card step replayed the previous person's envelope — their
// fileId and, worse, their document preview. The upload then failed with
// "fileId is not a ready file owned by you", which is the server refusing what
// the browser should never have offered.
//
// Without an owner there is nothing to prove the draft belongs to whoever is
// looking, so nothing is stored and nothing is replayed.
const storageKey = (key, ownerId) => `${STORAGE_PREFIX}u${ownerId}.${key}`;

// Anything belonging to somebody else, or written before keys carried an owner,
// is removed the moment a different person uses this tab. Recovery is a
// convenience; leaving another account's document sitting in the tab is not a
// trade worth making.
const purgeForeignPending = (ownerId) => {
  try {
    const mine = `${STORAGE_PREFIX}u${ownerId}.`;
    for (const k of Object.keys(sessionStorage)) {
      if (k.startsWith(STORAGE_PREFIX) && !k.startsWith(mine)) sessionStorage.removeItem(k);
    }
  } catch {
    // Private mode — nothing stored means nothing to purge.
  }
};

const savePending = (key, res, ownerId) => {
  if (!key || !ownerId || !res?.fileId) return;
  try {
    sessionStorage.setItem(
      storageKey(key, ownerId),
      JSON.stringify({ ...res, savedAt: Date.now() })
    );
  } catch {
    // Private mode / quota — recovery is a bonus, never a requirement.
  }
};

// Reading consumes the entry: it exists to survive exactly one interrupted session,
// and a value that lingers could later override a document the user has since saved.
const consumePending = (key, ownerId) => {
  if (!key || !ownerId) return null;
  try {
    const raw = sessionStorage.getItem(storageKey(key, ownerId));
    sessionStorage.removeItem(storageKey(key, ownerId));
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved?.fileId) return null;
    if (saved.savedAt && Date.now() - saved.savedAt > MAX_AGE_MS) return null;
    return saved;
  } catch {
    return null;
  }
};

export default function DocUploadDropzone({
  label = 'Drop or select a file',
  helperText,
  file,
  onUploaded,
  toast,
  disabled = false,
  accept,
  folder,
  persistKey,
}) {
  const resolvedAccept = accept || DEFAULT_ACCEPT;
  const isPdfOnly = resolvedAccept === PDF_ONLY_ACCEPT;
  // Extensions alongside the MIME patterns, so react-dropzone (and the OS picker)
  // still take a file whose type the browser left blank.
  const dropzoneAccept = isPdfOnly
    ? `${PDF_ONLY_ACCEPT},.pdf`
    : `${resolvedAccept},.pdf,.jpg,.jpeg,.png,.webp`;
  const rejectMessage = isPdfOnly ? 'Please choose a PDF file!' : 'Please choose a PDF or image file!';
  const resolvedHelper = helperText || (isPdfOnly
    ? 'PDF — drag here, or click to browse'
    : 'PDF or image — drag here, or click to browse');

  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Read through refs so the restore effect can run once on mount without
  // re-firing every time the parent re-creates its inline handler.
  const fileRef = useRef(file);
  const onUploadedRef = useRef(onUploaded);
  fileRef.current = file;
  onUploadedRef.current = onUploaded;

  // Whoever is signed in right now owns any draft this tab may replay.
  const { user: authUser } = useAuth();
  const ownerId = authUser?.id ?? null;

  useEffect(() => {
    if (ownerId) purgeForeignPending(ownerId);
  }, [ownerId]);

  useEffect(() => {
    if (!persistKey || !ownerId) return undefined;
    // Defer past the parent's own hydration effect, which seeds `file` from the
    // saved record. A document already on the record wins — the stored envelope
    // is only meant to fill a gap, not to overwrite server state.
    const timer = setTimeout(() => {
      if (fileRef.current) return;
      const saved = consumePending(persistKey, ownerId);
      if (saved) onUploadedRef.current?.(saved);
    }, 0);
    return () => clearTimeout(timer);
  }, [persistKey, ownerId]);

  const handleFiles = useCallback(
    (accepted, rejected) => {
      const picked = accepted?.[0];
      if (!picked || !matchesAccept(picked, resolvedAccept)) {
        if (picked || rejected?.length) toast?.('warning', rejectMessage);
        return;
      }
      setUploading(true);
      setProgress(0);
      fileUpload(
        picked,
        (res) => {
          setUploading(false);
          if (res?.error) {
            toast?.('warning', res.error);
            return;
          }
          savePending(persistKey, res, ownerId);
          onUploaded?.(res);
        },
        { onProgress: setProgress, ...(folder ? { folder } : {}) }
      );
    },
    [onUploaded, toast, resolvedAccept, rejectMessage, folder, persistKey, ownerId]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: dropzoneAccept,
    multiple: false,
    disabled: disabled || uploading,
    onDrop: handleFiles,
  });

  return (
    <Box sx={{ width: '100%' }}>
      {file ? (
        <Box sx={{ mb: 2 }}>
          <DocPreview file={file} />
        </Box>
      ) : null}

      <DropZone
        {...getRootProps()}
        sx={{
          ...(isDragActive && { opacity: 0.72 }),
          ...(isDragReject && {
            color: 'error.main',
            borderColor: 'error.light',
            bgcolor: 'error.lighter',
          }),
          ...((disabled || uploading) && { opacity: 0.6, cursor: 'not-allowed' }),
        }}
      >
        <input {...getInputProps()} />
        <Stack alignItems="center" spacing={1}>
          <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          <Typography variant="subtitle1">{file ? `Replace: ${label}` : label}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {resolvedHelper}
          </Typography>
        </Stack>
      </DropZone>

      {uploading && (
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Uploading… {progress}%
          </Typography>
        </Box>
      )}
    </Box>
  );
}
